/**
 * Screen 1 for the card-on-file (Flex) flow: one question, then what each hire costs.
 *
 * Flex has no contract, no hiring volume and nothing due up front (D1, D2), so the old flow's hires-a-year,
 * steady-or-bursts and annual-spend questions priced nothing and are not asked (S9). The one question that matters is
 * the salary band of the roles they hire most, because each hire is charged at its own band's price at offer. A role
 * above $500K is priced by hand (D4): that answer leads to a conversation, not to signup.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'antd';
import FlowShell from '../../components/flow-shell';
import { useSelfServe } from '../../context';
import { contactHref } from '../../utils/contact';
import {
  SALARY_BAND_LABEL,
  SalaryBand,
  SalaryBandChoice,
  flexQuoteFor,
} from '../../utils/flex';
import { FLEX_COPY } from '../../utils/flexCopy';
import styles from './index.module.css';

const BANDS: SalaryBand[] = ['under100k', 'from100kTo200k', 'from200kTo500k'];

const FlexQualify = () => {
  const navigate = useNavigate();
  const { flexQuote, completeFlexQualification } = useSelfServe();
  const [choice, setChoice] = useState<SalaryBandChoice | null>(
    flexQuote?.band ?? null,
  );

  const choose = (next: SalaryBandChoice) => {
    setChoice(next);
    const quote = flexQuoteFor(next);
    if (quote) completeFlexQualification(quote);
  };

  const quote = choice ? flexQuoteFor(choice) : null;

  return (
    <FlowShell
      step={1}
      title="See what each hire costs"
      subtitle="One question. No sales call required."
    >
      <div className={styles.thread} aria-live="polite">
        <p className={styles.fromAvrio}>{FLEX_COPY.salaryQuestion}</p>
        {choice && (
          <p className={styles.fromYou}>
            {choice === 'above500k'
              ? FLEX_COPY.salaryOptionAbove
              : SALARY_BAND_LABEL[choice]}
          </p>
        )}
      </div>

      <div className={styles.choices}>
        {BANDS.map((band) => (
          <Button
            key={band}
            onClick={() => choose(band)}
            className={styles.choice}
            type={choice === band ? 'primary' : 'default'}
          >
            {SALARY_BAND_LABEL[band]}
          </Button>
        ))}
        <Button
          onClick={() => choose('above500k')}
          className={styles.choice}
          type={choice === 'above500k' ? 'primary' : 'default'}
        >
          {FLEX_COPY.salaryOptionAbove}
        </Button>
      </div>

      {choice === 'above500k' && (
        <div className={styles.summary}>
          <h2 className={styles.summaryTitle}>{FLEX_COPY.aboveTitle}</h2>
          <p className={styles.note}>{FLEX_COPY.aboveBody}</p>
          <p className={styles.note}>
            <a
              href={contactHref(
                'Avrio for a role above $500K',
                'We are hiring for a role above $500K and would like a price.',
              )}
            >
              {FLEX_COPY.aboveLink}
            </a>
          </p>
        </div>
      )}

      {quote && (
        <div className={styles.summary}>
          <h2 className={styles.summaryTitle}>{FLEX_COPY.resultTitle}</h2>
          <p className={styles.note}>
            {FLEX_COPY.bandLine(quote.band, quote.pricePerHire)}
          </p>
          <p className={styles.caveat}>{FLEX_COPY.resultExplainer}</p>
          <p className={styles.caveat}>{FLEX_COPY.otherBandsNote}</p>
          <Button
            type="primary"
            size="large"
            onClick={() => navigate('/selfserve/account')}
            className={styles.cta}
          >
            Continue
          </Button>
        </div>
      )}
    </FlowShell>
  );
};

export default FlexQualify;
